import { AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

interface RiskFactor {
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  mitigation?: string;
}

interface RiskFactorsProps {
  factors?: string[] | RiskFactor[] | any[];
}

// Helper to map severity to Alert variant and optional classes
const getSeverityProps = (severity: RiskFactor['severity']) => {
  switch (severity) {
    case 'HIGH':
      return { 
        variant: 'destructive' as const, 
        icon: ShieldAlert,
        titleClass: "text-destructive" 
      };
    case 'MEDIUM':
      return { 
        variant: 'default' as const, 
        icon: AlertTriangle, 
        className: "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/40",
        titleClass: "text-yellow-700 dark:text-yellow-300"
      };
    case 'LOW':
      return { 
        variant: 'default' as const, 
        icon: Info, 
        className: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/40",
        titleClass: "text-blue-700 dark:text-blue-300"
       };
    default:
      return { 
        variant: 'default' as const, 
        icon: AlertTriangle,
        className: "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/40",
        titleClass: "text-yellow-700 dark:text-yellow-300"
       };
  }
};

// Enhanced parsing function to handle all possible formats
const parseRiskFactor = (factor: any): RiskFactor | null => {
  if (!factor) return null;

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('Processing factor:', factor, 'Type:', typeof factor);
  }

  // Case 1: Already a properly structured RiskFactor object
  if (typeof factor === 'object' && factor !== null && !Array.isArray(factor)) {
    // Check if it has the expected properties
    if (factor.severity && factor.description) {
      return {
        severity: String(factor.severity).toUpperCase() as RiskFactor['severity'],
        description: String(factor.description),
        mitigation: factor.mitigation ? String(factor.mitigation) : undefined,
      };
    }
    
    // Handle case where object has data but different property names
    if (factor.description || factor.desc) {
      return {
        severity: (String(factor.severity || factor.level || 'MEDIUM')).toUpperCase() as RiskFactor['severity'],
        description: String(factor.description || factor.desc || 'No description provided'),
        mitigation: factor.mitigation || factor.mit ? String(factor.mitigation || factor.mit) : undefined,
      };
    }
  }

  // Case 2: String that might be JSON or formatted text
  if (typeof factor === 'string') {
    const cleanFactor = factor.replace(/^RISK_FACTORS?\s*:\s*/i, '').trim();
    
    // Try to parse as JSON first
    if (cleanFactor.startsWith('{') && cleanFactor.endsWith('}')) {
      try {
        const parsed = JSON.parse(cleanFactor);
        if (parsed && typeof parsed === 'object') {
          return {
            severity: String(parsed.severity || 'MEDIUM').toUpperCase() as RiskFactor['severity'],
            description: String(parsed.description || cleanFactor),
            mitigation: parsed.mitigation ? String(parsed.mitigation) : undefined,
          };
        }
      } catch (e) {
        console.warn('Failed to parse JSON risk factor:', e);
        // Continue to text parsing
      }
    }

    // Try structured text parsing
    const patterns = [
      // Pattern 1: "- Severity: HIGH * Description: ... * Mitigation: ..."
      /^-?\s*Severity:\s*\[?(HIGH|MEDIUM|LOW)\]?\s*\*?\s*Description:\s*\[?([^\n\]]+?)\]?\s*\*?\s*Mitigation:\s*\[?([^\n\]]+?)\]?/im,
      // Pattern 2: "Severity: HIGH Description: ... Mitigation: ..."
      /Severity:\s*\[?(HIGH|MEDIUM|LOW)\]?.*?Description:\s*\[?([^\n]+?)\]?.*?(?:Mitigation:\s*\[?([^\n]+?)\]?)?/is,
      // Pattern 3: Just severity and description
      /(?:Severity:\s*)?\[?(HIGH|MEDIUM|LOW)\]?\s*[:\-]?\s*([^*\n]+?)(?:\s*\*?\s*Mitigation:\s*([^\n]+))?/i
    ];

    for (const pattern of patterns) {
      const match = cleanFactor.match(pattern);
      if (match) {
        return {
          severity: match[1].toUpperCase() as RiskFactor['severity'],
          description: match[2].trim(),
          mitigation: match[3] ? match[3].trim() : undefined,
        };
      }
    }

    // Final fallback: treat entire string as description with default severity
    if (cleanFactor.length > 0) {
      return {
        severity: 'MEDIUM',
        description: cleanFactor,
      };
    }
  }

  // Case 3: Handle other types by converting to string
  if (factor !== null && factor !== undefined) {
    return {
      severity: 'MEDIUM',
      description: String(factor),
    };
  }

  return null;
};

export function RiskFactors({ factors = [] }: RiskFactorsProps) {
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('RiskFactors - Raw input:', factors);
    console.log('RiskFactors - Input type:', typeof factors, 'Is array:', Array.isArray(factors));
  }

  if (!factors?.length) {
    return (
      <div className="text-sm text-muted-foreground italic">
        <p>No significant risk factors identified.</p>
      </div>
    );
  }

  // Parse all factors
  const parsedFactors: RiskFactor[] = factors
    .map(parseRiskFactor)
    .filter((factor): factor is RiskFactor => factor !== null);

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('RiskFactors - Parsed factors:', parsedFactors);
  }

  if (parsedFactors.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        <p>No valid risk factors could be parsed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {parsedFactors.map((factor, index) => {
        const { variant, icon: Icon, className, titleClass } = getSeverityProps(factor.severity);
        return (
          <Alert key={index} variant={variant} className={cn(className)}> 
            <Icon className="h-4 w-4" />
            <AlertTitle className={cn(titleClass)}>{factor.severity}</AlertTitle>
            <AlertDescription className="pt-1">
              <div className="space-y-2">
                <p>{factor.description}</p>
                {factor.mitigation && (
                  <p className="text-sm text-muted-foreground border-l-2 border-slate-200 pl-3">
                    <span className="font-medium text-foreground/80">Mitigation: </span>
                    {factor.mitigation}
                  </p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
